import type { OutputState, PlacementState } from '../types.js';
import type { ProgressionSnapshot } from './progression.js';

const PREFIX = 'vizipedia-city-builder:';

export const SAVE_VERSION = 2;

let suppressed = false;

/**
 * Stops persist() from resurrecting the cleared save when the restart
 * reload fires pagehide. Lives in module memory, so it resets per load.
 */
export function suppressSaves(): void {
    suppressed = true;
}

export function savesSuppressed(): boolean {
    return suppressed;
}

export interface SavedPlacement {
    object: string;
    col: number;
    row: number;
    state: PlacementState;
}

export interface SavedSession {
    remainingMs: number;
}

export interface SavedGame {
    version: number;
    savedAt: number;
    balances: Record<string, number>;
    progression: ProgressionSnapshot;
    placements: SavedPlacement[];
    slots?: Record<string, number>;
    session?: SavedSession;
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function isNonNegativeNumber(value: unknown): value is number {
    return isFiniteNumber(value) && value >= 0;
}

function isNonNegativeInteger(value: unknown): value is number {
    return Number.isInteger(value) && (value as number) >= 0;
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function parseBalances(value: unknown): Record<string, number> | null {
    if (!isRecord(value)) return null;

    const balances: Record<string, number> = {};
    for (const [id, amount] of Object.entries(value)) {
        if (!id || !isNonNegativeNumber(amount)) return null;
        balances[id] = amount;
    }
    return balances;
}

function parseOutput(value: unknown): OutputState | null {
    if (!isRecord(value) || typeof value.resource !== 'string' || !value.resource) return null;
    if (!isNonNegativeNumber(value.stored)) return null;

    const capacity = value.capacity;
    const remainingMs = value.remainingMs;
    if (capacity !== null && capacity !== undefined && !isNonNegativeNumber(capacity)) return null;
    if (remainingMs !== null && remainingMs !== undefined && !isNonNegativeNumber(remainingMs)) return null;

    return {
        resource: value.resource,
        stored: value.stored,
        capacity: capacity == null ? null : capacity,
        remainingMs: remainingMs == null ? null : remainingMs,
    };
}

function parsePlacementState(value: unknown): PlacementState | null {
    if (!isRecord(value) || !isNonNegativeInteger(value.level) || value.level < 1) return null;
    if (!Array.isArray(value.outputs)) return null;

    const constructionMs = value.constructionMs;
    const constructionTotalMs = value.constructionTotalMs;
    if (constructionMs !== null && constructionMs !== undefined && !isNonNegativeNumber(constructionMs)) {
        return null;
    }
    if (
        constructionTotalMs !== null
        && constructionTotalMs !== undefined
        && !isNonNegativeNumber(constructionTotalMs)
    ) {
        return null;
    }

    const outputs: OutputState[] = [];
    for (const output of value.outputs) {
        const parsed = parseOutput(output);
        if (!parsed) return null;
        outputs.push(parsed);
    }

    return {
        level: value.level,
        outputs,
        constructionMs: constructionMs == null ? null : constructionMs,
        constructionTotalMs: constructionTotalMs == null ? null : constructionTotalMs,
    };
}

function parseProgression(value: unknown): ProgressionSnapshot | null {
    if (!isRecord(value)) return null;
    if (!isNonNegativeInteger(value.level) || value.level < 1) return null;
    if (!isStringArray(value.firedMilestones) || !isStringArray(value.claimedQuests)) return null;
    if (value.unlockedObjects !== undefined && !isStringArray(value.unlockedObjects)) return null;

    return {
        level: value.level,
        firedMilestones: [...value.firedMilestones],
        claimedQuests: [...value.claimedQuests],
        ...(value.unlockedObjects ? { unlockedObjects: [...value.unlockedObjects] } : {}),
    };
}

function parsePlacements(value: unknown): SavedPlacement[] | null {
    if (!Array.isArray(value)) return null;

    const placements: SavedPlacement[] = [];
    for (const entry of value) {
        if (!isRecord(entry) || typeof entry.object !== 'string' || !entry.object) return null;
        if (!isNonNegativeInteger(entry.col) || !isNonNegativeInteger(entry.row)) return null;

        const state = parsePlacementState(entry.state);
        if (!state) return null;
        placements.push({ object: entry.object, col: entry.col, row: entry.row, state });
    }
    return placements;
}

function parseSlots(value: unknown): Record<string, number> | undefined | null {
    if (value === undefined) return undefined;
    if (!isRecord(value)) return null;

    const slots: Record<string, number> = {};
    for (const [id, amount] of Object.entries(value)) {
        if (!id || !isNonNegativeInteger(amount)) return null;
        slots[id] = amount;
    }
    return slots;
}

function parseSession(value: unknown): SavedSession | undefined | null {
    if (value === undefined) return undefined;
    if (!isRecord(value) || !isNonNegativeNumber(value.remainingMs)) return null;
    return { remainingMs: value.remainingMs };
}

/** Parse and validate persisted state, accepting saves from before versioning. */
export function parseSavedGame(raw: string): SavedGame | null {
    try {
        const parsed: unknown = JSON.parse(raw);
        if (!isRecord(parsed)) return null;

        const version = parsed.version === undefined ? 1 : parsed.version;
        if (!isNonNegativeInteger(version) || (version !== 1 && version !== SAVE_VERSION)) return null;
        if (!isNonNegativeNumber(parsed.savedAt)) return null;

        const balances = parseBalances(parsed.balances);
        const progression = parseProgression(parsed.progression);
        const placements = parsePlacements(parsed.placements);
        const slots = parseSlots(parsed.slots);
        const session = parseSession(parsed.session);
        if (!balances || !progression || !placements || slots === null || session === null) return null;

        return {
            version,
            savedAt: parsed.savedAt,
            balances,
            progression,
            placements,
            ...(slots === undefined ? {} : { slots }),
            ...(session === undefined ? {} : { session }),
        };
    } catch {
        return null;
    }
}

function keyFor(gameId: string): string {
    return `${PREFIX}${gameId}`;
}

function storage(): Storage | null {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

export function save(gameId: string, game: SavedGame): void {
    const store = storage();
    if (!store) return;

    try {
        store.setItem(keyFor(gameId), JSON.stringify(game));
    } catch {
        // Storage is optional.
    }
}

export function load(gameId: string): SavedGame | null {
    const store = storage();
    if (!store) return null;

    let raw: string | null = null;
    try {
        raw = store.getItem(keyFor(gameId));
    } catch {
        return null;
    }
    if (!raw) return null;

    return parseSavedGame(raw);
}

export function clear(gameId: string): void {
    const store = storage();
    if (!store) return;

    try {
        store.removeItem(keyFor(gameId));
    } catch {
        // Storage is optional.
    }
}

export function elapsedSince(savedAt: number, now = Date.now()): number {
    return Math.max(0, now - savedAt);
}
